/**
 * Depósito dos quadros baixados em DISCO (Cache Storage do navegador), fora
 * da memória RAM.
 *
 * Por quê: com os JPEGs guardados como Blob na RAM, a memória crescia com o
 * PERÍODO × a RESOLUÇÃO — 48 h em 7200 px são ~3 GB, e o orçamento de RAM
 * cortava o loop nos quadros mais recentes (121 de 288 num desktop de 8 GB,
 * medido em 21/09/2026). No disco, a RAM passa a depender só da JANELA de
 * quadros decodificados; o limite dos arquivos é o espaço que o navegador
 * concede ao site (`navigator.storage.estimate`), tipicamente dezenas de GB.
 *
 * Medido (Chrome, Mac, quadro de 7200 px com 20,7 MB): gravar 45 ms, reler
 * 19 ms — contra 247 ms para decodificar. O disco não é o gargalo.
 *
 * Bônus: os quadros ficam de uma visita para a outra. Reabrir o portal não
 * baixa de novo o que ainda está nas últimas 48 h.
 */
import { parseStarStamp } from './stamp';
import { isImageWidth, type ImageWidth } from './star';

/** O que o cache de quadros precisa de um depósito (injetável nos testes). */
export interface BlobStore {
  /** O arquivo guardado, ou null se não houver (ou o navegador o apagou). */
  get(url: string): Promise<Blob | null>;
  /** Guarda o arquivo. Rejeita quando falta espaço (`QuotaExceededError`). */
  put(url: string, blob: Blob): Promise<void>;
  delete(url: string): Promise<void>;
  /** URLs guardadas. */
  keys(): Promise<string[]>;
}

/** Nome do depósito. Mudar a versão abandona o anterior (ver `openDiskStore`). */
export const DISK_CACHE_NAME = 'amaview-quadros-v1';

/** Depósito sobre o Cache Storage, ou null se o navegador não oferecer (ex.: janela anônima do Firefox). */
export async function openDiskStore(): Promise<BlobStore | null> {
  if (typeof caches === 'undefined') return null;
  try {
    const cache = await caches.open(DISK_CACHE_NAME);
    return {
      async get(url) {
        const r = await cache.match(url);
        return r ? r.blob() : null;
      },
      async put(url, blob) {
        await cache.put(url, new Response(blob, { headers: { 'content-type': blob.type || 'image/jpeg' } }));
      },
      async delete(url) {
        await cache.delete(url);
      },
      async keys() {
        return (await cache.keys()).map((r) => r.url);
      },
    };
  } catch {
    return null;
  }
}

/** Fração do espaço concedido que os quadros podem ocupar. */
export const DISK_QUOTA_FRACTION = 0.8;
/** Teto absoluto, para não tomar o disco de quem tem muito espaço livre. */
export const DISK_MAX_BYTES = 12e9;

/**
 * Espaço em disco para os quadros, a partir do que o navegador concede ao
 * site (`navigator.storage.estimate().quota`). Null quando o navegador não
 * informa — sem saber o limite, os quadros continuam na memória.
 */
export function diskBudgetBytes(quota: number | undefined | null): number | null {
  if (typeof quota !== 'number' || !Number.isFinite(quota) || quota <= 0) return null;
  return Math.min(DISK_MAX_BYTES, Math.floor(quota * DISK_QUOTA_FRACTION));
}

/** Espaço concedido ao site, ou undefined se o navegador não informa. */
export async function estimateQuota(): Promise<number | undefined> {
  try {
    const e = await navigator.storage?.estimate?.();
    return e?.quota;
  } catch {
    return undefined;
  }
}

/** Produto, instante e largura de um arquivo de quadro do STAR, pelo nome. */
export function parseFrameUrl(url: string): { productId: string; time: number; width: ImageWidth } | null {
  const m = /\/(\d{11})_GOES19-ABI-nsa-([^/]+)-(\d+)x\d+\.jpg$/.exec(url);
  if (!m) return null;
  const time = parseStarStamp(m[1]!);
  const width = Number(m[3]);
  if (time === null || !isImageWidth(width)) return null;
  return { productId: m[2]!, time, width };
}

/**
 * O que continua guardado no disco depois que sai do plano.
 *
 * Voltar de "3 h" para "48 h" não deve baixar tudo de novo: quadros na MESMA
 * largura e dos MESMOS produtos do plano atual, dentro das últimas 48 h, ficam.
 * Quando o plano foi cortado por falta de espaço, fica só o que está nele —
 * guardar sobras seria roubar o espaço que o loop precisa.
 */
export interface DiskRetention {
  /** URLs do plano atual (inclusive as da largura que ainda toca numa troca). */
  wanted: ReadonlySet<string>;
  width: ImageWidth;
  productIds: ReadonlySet<string>;
  /** Instante mais antigo que ainda vale guardar (início das últimas 48 h). */
  since: number;
  /** O plano foi cortado por falta de espaço. */
  truncated: boolean;
}

export function retainOnDisk(url: string, r: DiskRetention): boolean {
  if (r.wanted.has(url)) return true;
  if (r.truncated) return false;
  const f = parseFrameUrl(url);
  return !!f && f.width === r.width && r.productIds.has(f.productId) && f.time >= r.since;
}
